import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@control-center/ui';

import { useCreateClient } from '../../hooks/use-clients';
import { extractErrorMessage } from '../../lib/errors';
import type { ClientEntity } from '../../lib/clients-api';
import { ClientForm, type ClientFormSubmit } from './ClientForm';

interface NewClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (client: ClientEntity) => void;
}

export function NewClientDialog({
  open,
  onOpenChange,
  onCreated,
}: NewClientDialogProps): JSX.Element {
  const create = useCreateClient();

  async function handleSubmit(values: ClientFormSubmit): Promise<void> {
    try {
      const created = await create.mutateAsync(values);
      toast.success('Cliente criado.');
      onCreated(created);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos criar o cliente.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(800px,calc(100vw-32px))] max-h-[calc(100vh-48px)] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel shadow-lg p-7',
            'focus:outline-none',
          )}
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
              Novo cliente
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <ClientForm
            isPending={create.isPending}
            submitLabel="Criar cliente"
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
